import {
  RoutineType,
  useCreateMutation,
  useCustomQuery,
  useEmbeddedRoutines,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../../loadingIndicator';
import { AlertForm } from './alertForm';

const GET_GROUP_WITH_ROUTINES = `
  query GetGroupWithRoutines($id: ID!, $groupFilter: WorkspaceRoutineFilter!) {
    getGroup(id: $id) {
      id
      name
      workspace {
        id
        name
      }
    }
    findWorkspaceRoutines(filter: $groupFilter) {
      items {
        id
        name
        type
        order
        config
      }
    }
  }
`;

const CREATE_WORKFLOW = `
  mutation CreateWorkflow($input: CreateWorkflowInput!) {
    workflow: createWorkflow(input: $input) {
      id
      name
    }
  }
`;

type GroupAlertFormProps = {
  groupId: string;
  onClose: () => void;
};

export const GroupAlertForm = ({
  groupId,
  onClose,
}: GroupAlertFormProps) => {
  const { t } = useTranslation();

  const { data: groupData, isLoading } = useCustomQuery({
    query: GET_GROUP_WITH_ROUTINES,
    cacheKey: ['group-routines', groupId],
    variables: {
      id: groupId,
      groupFilter: { 
        groups: { eq: groupId },
        deactivated: { ne: true }
      },
    },
  });

  const [defaultObject, setDefaultObject] = useState<any>(null);
  const [counterRoutines, setCounterRoutines] = useState<WorkspaceRoutine[]>([]);
  const [availableRoutines, setAvailableRoutines] = useState<WorkspaceRoutine[]>([]);
  const [currentGroup, setCurrentGroup] = useState<any>(null);

  const directRoutines: WorkspaceRoutine[] = (groupData as any)?.findWorkspaceRoutines?.items || [];
  const { routinesWithEmbedded, isEmbeddedRoutinesLoading } = useEmbeddedRoutines(directRoutines);

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  useEffect(() => {
    if (!isLoading && !isEmbeddedRoutinesLoading && groupData) {
      const group = (groupData as any)?.getGroup;
      
      setCurrentGroup(group);
      
      const counterRoutines = routinesWithEmbedded.filter(
        (routine: any) => routine.type === RoutineType.Counter
      );
      setCounterRoutines(counterRoutines);
      setAvailableRoutines(routinesWithEmbedded);

      const groupName = group.name;
      const daysStartDate = moment().format('MM/DD/YYYY');

      const defaultValues = {
        alertLabel: '',
        alertLabelPlaceholder: `${t('alerts.alert')}: ${groupName}`,
        startDate: undefined,
        endDate: undefined,
        minValue: undefined,
        maxValue: undefined,
        alertType: undefined,
        secondAlertType: undefined,
        days: undefined,
        daysStartDate: daysStartDate,
        completions: undefined,
        daysSkipped: undefined,
        onDate: undefined,
        intervalRoutineId: undefined,
        onInterval: undefined,
        onIntervalStart: undefined,
      };

      setDefaultObject(defaultValues);
    }
  }, [isLoading, isEmbeddedRoutinesLoading, groupData, routinesWithEmbedded, t]);

  const submitAlert = async (input: any) => {
    await createWorkflow(
      { input },
      {
        onSuccess: async () => {
          onClose();
        },
      }
    );
  };

  const isLoadingData = isLoading || isEmbeddedRoutinesLoading;

  return isLoadingData || !defaultObject || !currentGroup ? (
    <LoadingIndicator />
  ) : (
    <AlertForm
      defaultValues={defaultObject}
      counterRoutines={counterRoutines}
      group={currentGroup}
      availableRoutines={availableRoutines}
      submitAlert={submitAlert}
      onClose={onClose}
      headerTitle="alerts.newAlert"
      backButtonLabel={t('topNav.back')}
      openDropdownOnLoad={false}
    />
  );
};
